import { ALERTS, ANALYSIS } from './alerts'
import { OEE_ATTRIBUTION, OEE_PERIOD_DATA, OEE_TARGET } from './oee'
import { allWorkOrders, isOpenOrder } from './operations'
import { HANDOVER_REPORT, SELF, TASKS, TASK_PEOPLE } from './home'
import { enterpriseStats, siteRollups } from './executive'

/**
 * A Forge AI conversation, as the API will return it.
 *
 * SYNTHETIC, and deliberately not routed through the i18n layer: assistant
 * output is generated by the model in the org's `aiOutputLocale`, so it arrives
 * as content and is rendered as content. Only the furniture around it — role
 * labels, timestamps, the verification disclaimer — goes through `t()`. Putting
 * model output in a translation file would be pretending the client authored it.
 *
 * Messages are blocks rather than strings so the renderer never parses markup,
 * and so an evidence table inside a reply is structured data the UI can style
 * consistently with every other evidence table in the product.
 */

export const COPILOT_THREAD = {
  sessionId: 'CPS-8841',
  context: { alertId: 'ALR-4471', assetId: 'FDY-BM-03', siteId: 'site_findlay' },
  messages: [
    {
      id: 'm1',
      author: 'user',
      at: '02:41',
      blocks: [{ type: 'text', text: 'Why is BM-03 flagged?' }],
    },
    {
      id: 'm2',
      author: 'ai',
      at: '02:41',
      blocks: [
        {
          type: 'text',
          text: 'Vibration RMS on the discharge bearing has risen for 72 hours — 0.013 in/s per hour, about 13.7× the trailing 30-day baseline for this product run. Bearing-housing temperature rose with it at r = 0.87, which is what rules out a sensor fault: a failing accelerometer does not warm the housing.',
        },
        {
          type: 'evidence',
          rows: [
            { key: 'detectors', value: 'Δz 11.12σ · CUSUM 2.31' },
            { key: 'cascade', value: '4 sibling sensors' },
            { key: 'regression', value: 'r² 0.998 · η 4.5h' },
            { key: 'logbook', value: '02:14 · cell 3 noise' },
          ],
        },
        {
          type: 'text',
          text: 'The operator logbook entry at 02:14 reports knocking audible at cell 3, which is consistent with the mechanical reading rather than an instrumentation one.',
        },
      ],
    },
    {
      id: 'm3',
      author: 'user',
      at: '02:43',
      blocks: [{ type: 'text', text: 'Can it wait until the weekend stop?' }],
    },
    {
      id: 'm4',
      author: 'ai',
      at: '02:43',
      blocks: [
        {
          type: 'verdict',
          tone: 'red',
          text: 'No. At the current slope the trip threshold of 0.49 in/s is about 4 hours 30 minutes away. The weekend stop is 61 hours out.',
        },
        {
          type: 'text',
          text: 'Two comparable events exist in the enterprise. FDY-BM-07 in March and PNQ-BM-02 last year both ran to a planned replacement after being caught at a similar slope, and neither took unplanned downtime. Both were pulled inside the same shift they were flagged.',
        },
        {
          type: 'text',
          text: 'I have drafted WO-7734 against the next planned stop, reserved the bearing and both seals, and left it unreleased. Lockout/tagout is required before this bearing is touched — that is your sign-off, not mine.',
        },
      ],
    },
  ],
  suggestions: ['Show the 72-hour trace', 'What else is on this line?', 'Draft the handover note'],
}

/* ══════════════════════════════════════════════════════════════════════════
   The Copilot SCREEN
   ══════════════════════════════════════════════════════════════════════════

   `COPILOT_THREAD` above is the dock's conversation — grounded on ALR-4471 and
   read by `AssistantDock`. Nothing below changes it.

   This half serves `/app/copilot`: four agent modes, their prompt chips, and a
   canned answer per chip. It is deliberately a LOOKUP and not a reply engine.
   There is no model behind a fixture, and the two honest options are a small
   set of prepared answers or a matcher that pretends to more understanding
   than it has. This is the first, and the fallback says so in as many words
   rather than improvising something plausible about a plant.

   What the answers are NOT is invented. Every figure in them is read from the
   fixtures the rest of the product renders — the alert list, the OEE module,
   the work-order board — so an answer here cannot contradict the screen it
   sends you to. `verify:mocks` asserts the ones that matter.
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * The four agent modes, from the design's left rail.
 *
 * A mode is not decoration: it swaps the prompt chips, which is the whole of
 * what it does in the prototype too. Keeping that and nothing more is honest —
 * a "Troubleshooting" mode that also changed the model's behaviour would be a
 * claim this layer cannot back.
 */
export const COPILOT_MODES = ['explore', 'trouble', 'maint', 'alerts']

const round = (value, digits = 1) => {
  const factor = 10 ** digits
  return Math.round(value * factor) / factor
}

const openAlerts = () => ALERTS.filter((alert) => alert.status !== 'resolved')

/**
 * The prepared answers, one per prompt chip.
 *
 * `blocks` are the shape `ChatThread` already renders — `text`, `evidence` and
 * `verdict` — so this reuses the assistant surface the dock and the public page
 * use rather than introducing a fourth way to draw a reply.
 *
 * `reasoning` is the design's "Thought process" disclosure. Its steps name the
 * routes an answer would really have come from, which is the part of that
 * affordance worth keeping: it is a provenance claim, and a provenance claim
 * that named nothing would be theatre.
 *
 * Built as a FUNCTION rather than a constant so the figures are read at request
 * time. A module-level snapshot would freeze "3 critical" into the fixture and
 * drift the moment an alert was acknowledged.
 */
export function copilotAnswers() {
  const alerts = openAlerts()
  const critical = alerts.filter((alert) => alert.severity === 'critical')
  const flagship = ALERTS.find((alert) => alert.id === 'ALR-4471')
  const compressor = ALERTS.find((alert) => alert.id === 'ALR-4470')
  const orders = allWorkOrders()
  const open = orders.filter(isOpenOrder)
  const week = OEE_PERIOD_DATA.week

  /* Which asset carries the most open anomalies. Found, not asserted — the
     prototype answers "C-12" to this question whatever is actually wrong. */
  const worstAsset = (() => {
    const tally = new Map()
    for (const alert of alerts) {
      tally.set(alert.assetName, (tally.get(alert.assetName) ?? 0) + 1)
    }
    return [...tally.entries()].sort((a, b) => b[1] - a[1])[0] ?? null
  })()

  return [
    {
      id: 'ans_bm03',
      mode: 'trouble',
      prompt: `Why is ${flagship.assetName} flagged?`,
      reasoning: ['resolvedAsset', 'queriedSignals', 'traversedGraph', 'composed'],
      blocks: [
        {
          type: 'text',
          text: `${flagship.sensor} on the ${flagship.component.toLowerCase()} is reading ${flagship.reading.value} ${flagship.reading.unit} against a ${flagship.threshold.value} ${flagship.threshold.unit} limit, up from a ${flagship.baseline.value} ${flagship.baseline.unit} baseline. ${flagship.cascadeSensors} sibling sensors agree, and bearing-housing temperature is rising with it at r = ${flagship.correlation} — which is what rules out a sensor fault. A failing accelerometer does not warm the housing.`,
        },
        {
          type: 'evidence',
          rows: [
            { key: 'detectors', value: flagship.detectors.map((d) => `${d.name} ${d.score}${d.unit ?? ''}`).join(' · ') },
            { key: 'cascade', value: `${flagship.cascadeSensors} sibling sensors` },
            { key: 'correlation', value: `temp r = ${flagship.correlation}` },
            { key: 'logbook', value: '02:14 · cell 3 noise' },
          ],
        },
        {
          type: 'verdict',
          tone: 'red',
          text: `At the current slope the ${flagship.threshold.value} ${flagship.threshold.unit} threshold is about ${flagship.etaHours} hours away.`,
        },
      ],
    },
    {
      id: 'ans_c12',
      mode: 'trouble',
      prompt: `What is wrong with ${compressor.assetName}?`,
      reasoning: ['resolvedAsset', 'queriedSignals', 'composed'],
      blocks: [
        {
          type: 'text',
          text: `${compressor.sensor} on the ${compressor.component.toLowerCase()} has gone flat at ${compressor.reading.value} ${compressor.reading.unit} against a ${compressor.baseline.value} ${compressor.baseline.unit} baseline, while its neighbours keep reporting. A frozen signal with live siblings is an instrumentation fault until proven otherwise — the tag, the wiring or the transmitter, not the pump.`,
        },
        {
          type: 'verdict',
          tone: 'red',
          text: `${compressor.id} is ${compressor.severity} and estimated ${compressor.etaHours} hours from consequence. It is on ${compressor.lineName}.`,
        },
      ],
    },
    {
      id: 'ans_open_anomalies',
      mode: 'alerts',
      prompt: 'Which anomalies are still open?',
      reasoning: ['queriedAlerts', 'composed'],
      blocks: [
        {
          type: 'text',
          text: `${alerts.length} anomalies are open across the three plants, ${critical.length} of them critical. The critical ones are ${critical.map((alert) => `${alert.id} on ${alert.assetName}`).join(', ')}.`,
        },
        {
          type: 'evidence',
          rows: critical.slice(0, 4).map((alert) => ({
            key: 'anomaly',
            value: `${alert.id} · ${alert.sensor} · ${alert.assetName}`,
          })),
        },
      ],
    },
    {
      id: 'ans_worst_asset',
      mode: 'alerts',
      prompt: 'Which machine has the most anomalies?',
      reasoning: ['queriedAlerts', 'composed'],
      blocks: [
        {
          type: 'text',
          text: worstAsset
            ? `${worstAsset[0]}, with ${worstAsset[1]} open ${worstAsset[1] === 1 ? 'anomaly' : 'anomalies'}. Across the plant the open count is ${alerts.length}, so that is ${round((worstAsset[1] / alerts.length) * 100, 0)}% of everything currently raised.`
            : 'Nothing is open at the moment.',
        },
      ],
    },
    {
      id: 'ans_oee',
      mode: 'explore',
      prompt: 'Summarise this week’s OEE',
      reasoning: ['queriedOee', 'composed'],
      blocks: [
        {
          type: 'text',
          text: `OEE is running at ${week.oee.value}% for the week, ${week.oee.deltaPts >= 0 ? 'up' : 'down'} ${Math.abs(week.oee.deltaPts)} points. Availability is ${week.availability.value}%, performance ${week.performance.value}% and quality ${week.quality.value}% — so availability is where the loss is, which is downtime rather than speed or scrap.`,
        },
        {
          type: 'evidence',
          rows: [
            { key: 'availability', value: `${week.availability.value}%` },
            { key: 'performance', value: `${week.performance.value}%` },
            { key: 'quality', value: `${week.quality.value}%` },
          ],
        },
      ],
    },
    {
      id: 'ans_worst_health',
      mode: 'explore',
      prompt: 'Which machines are in the worst state?',
      reasoning: ['queriedAssets', 'queriedAlerts', 'composed'],
      blocks: [
        {
          type: 'text',
          text: `${critical.map((alert) => alert.assetName).join(', ')} — each carrying a critical anomaly. ${critical[0] ? `${critical[0].assetName} is the nearest to consequence at about ${critical[0].etaHours} hours.` : ''}`,
        },
      ],
    },
    {
      id: 'ans_open_wos',
      mode: 'maint',
      prompt: 'Summarise the open work orders',
      reasoning: ['queriedWorkOrders', 'composed'],
      blocks: [
        {
          type: 'text',
          text: `${open.length} of ${orders.length} work orders are open. ${open.filter((order) => order.priority === 'critical').length} are critical, and ${orders.filter((order) => order.authoredBy === 'ai').length} of the total were drafted by Forge AI and are waiting on a human to commit them.`,
        },
        {
          type: 'evidence',
          rows: open
            .slice(0, 4)
            .map((order) => ({ key: 'workOrder', value: `${order.id} · ${order.state} · ${order.priority}` })),
        },
      ],
    },
    /* ── The nine questions the HOME composer offers ──────────────────
       `BRIEFING_SUGGESTIONS` puts three chips under the greeting per role, and
       the composer now hands whatever is typed to this screen. Every one of
       those nine used to miss and get the fallback — which is a screen that
       answers nothing to anybody who arrived the ordinary way. They are
       answered here, verbatim, and `verify:mocks` asserts it stays that way. */
    {
      id: 'ans_oee_down',
      mode: 'trouble',
      prompt: 'Why is OEE down?',
      reasoning: ['queriedOee', 'queriedAlerts', 'composed'],
      blocks: [
        {
          type: 'text',
          text: `Availability, not speed or scrap. The month runs ${week.oee.value <= OEE_TARGET ? 'below' : 'above'} target at ${OEE_PERIOD_DATA.month.oee.value}% against ${OEE_TARGET}%, with performance at ${OEE_PERIOD_DATA.month.performance.value}% and quality at ${OEE_PERIOD_DATA.month.quality.value}% — both close to where they should be. Availability is ${OEE_PERIOD_DATA.month.availability.value}%, and that is the whole of the gap.`,
        },
        { type: 'text', text: OEE_ATTRIBUTION.month },
      ],
    },
    {
      id: 'ans_watch_tonight',
      mode: 'trouble',
      prompt: 'What should I watch tonight?',
      reasoning: ['queriedAlerts', 'queriedSignals', 'composed'],
      blocks: [
        {
          type: 'text',
          text: (() => {
            if (!critical.length) return 'Nothing critical is open. The shift is quiet.'
            /* Not every alert carries an estimate — a frozen signal has no
               slope to project — so the ones that do are named with their
               clock and the rest without, rather than printing "null h out". */
            const ordered = [...critical].sort(
              (a, b) => (a.etaHours ?? Infinity) - (b.etaHours ?? Infinity),
            )
            return `${ordered
              .map((alert) =>
                alert.etaHours
                  ? `${alert.assetName} (${alert.id}, about ${alert.etaHours} h out)`
                  : `${alert.assetName} (${alert.id}, no estimate yet)`,
              )
              .join(', ')}. Those are the ones with a clock on them.`
          })(),
        },
        {
          type: 'verdict',
          tone: critical.length ? 'amber' : 'green',
          text: critical.length
            ? `${critical[0].assetName} first — ${critical[0].sensor} is the nearest to its limit.`
            : 'No machine is near a threshold tonight.',
        },
      ],
    },
    {
      id: 'ans_my_tasks',
      mode: 'maint',
      prompt: 'Show my tasks',
      reasoning: ['queriedWorkOrders', 'composed'],
      blocks: [
        {
          type: 'text',
          text: (() => {
            const mine = TASKS.filter((task) => task.assignee === SELF && task.status !== 'done')
            const who = TASK_PEOPLE.find((person) => person.id === SELF)?.name ?? 'you'
            return mine.length
              ? `${mine.length} open against ${who}. ${mine
                  .map((task) => `${task.title} (${task.priority})`)
                  .join('; ')}.`
              : `Nothing open against ${who}.`
          })(),
        },
      ],
    },
    {
      id: 'ans_approvals',
      mode: 'maint',
      prompt: 'What needs my approval?',
      reasoning: ['queriedWorkOrders', 'composed'],
      blocks: [
        {
          type: 'text',
          text: (() => {
            const waiting = orders.filter((order) => order.state === 'pendingApproval')
            const drafts = orders.filter((order) => order.state === 'draft')
            return waiting.length || drafts.length
              ? `${waiting.length} work ${waiting.length === 1 ? 'order is' : 'orders are'} waiting on approval and ${drafts.length} ${drafts.length === 1 ? 'is' : 'are'} still in draft. ${[...waiting, ...drafts].map((order) => order.id).join(', ')}.`
              : 'Nothing is waiting on you.'
          })(),
        },
        {
          type: 'verdict',
          tone: 'amber',
          text: 'Lockout/tagout applies to anything touching a bearing. That sign-off is yours, not mine.',
        },
      ],
    },
    {
      id: 'ans_handover_note',
      mode: 'maint',
      prompt: 'Draft the handover note',
      reasoning: ['queriedAlerts', 'queriedWorkOrders', 'composed'],
      blocks: [
        { type: 'text', text: HANDOVER_REPORT.summary },
        {
          type: 'text',
          text: `Carrying ${critical.length} critical and ${alerts.length - critical.length} lesser anomalies, with ${open.length} work orders open. The Shifts screen will compose this against the running shift and let you file it.`,
        },
      ],
    },
    {
      id: 'ans_last_shift',
      mode: 'alerts',
      prompt: 'What did the last shift leave me?',
      reasoning: ['queriedAlerts', 'traversedGraph', 'composed'],
      blocks: [
        {
          type: 'text',
          text: `${HANDOVER_REPORT.carried.length} anomalies were handed across the boundary: ${HANDOVER_REPORT.carried.map((entry) => `${entry.id} — ${entry.description}`).join('; ')}.`,
        },
        {
          type: 'evidence',
          rows: HANDOVER_REPORT.production.map((row) => ({ key: row.key, value: row.value })),
        },
      ],
    },
    {
      id: 'ans_site_risk',
      mode: 'alerts',
      prompt: 'Which plant is at risk?',
      reasoning: ['queriedAssets', 'queriedAlerts', 'composed'],
      blocks: [
        {
          type: 'text',
          text: (() => {
            const sites = siteRollups()
            const risk = [...sites].sort(
              (a, b) =>
                b.criticalAlerts - a.criticalAlerts ||
                b.downtimeRiskHours - a.downtimeRiskHours,
            )[0]
            if (!risk) return 'No plant is carrying a critical anomaly.'
            /* "More than the others combined" is COMPUTED, not asserted. It
               happens to be true today and would quietly stop being true the
               moment an alert moved. */
            const others = sites
              .filter((site) => site.id !== risk.id)
              .reduce((sum, site) => sum + site.downtimeRiskHours, 0)
            const comparison =
              risk.downtimeRiskHours > others
                ? ' — more than the other two plants carry between them'
                : ''
            return `${risk.name}. ${risk.oee}% OEE with ${risk.criticalAlerts} critical ${risk.criticalAlerts === 1 ? 'anomaly' : 'anomalies'} open and ${risk.downtimeRiskHours} hours of downtime exposure${comparison}.`
          })(),
        },
      ],
    },
    {
      id: 'ans_compare_sites',
      mode: 'explore',
      prompt: 'Compare the three plants',
      reasoning: ['queriedAssets', 'queriedOee', 'composed'],
      blocks: [
        {
          type: 'text',
          text: (() => {
            const sites = siteRollups()
            const stats = enterpriseStats(sites)
            return `The estate averages ${stats.oee}% against an ${stats.target}% target, ${Math.abs(stats.gapPts)} points short. ${[...sites]
              .sort((a, b) => b.oee - a.oee)
              .map((site) => `${site.name} ${site.oee}%`)
              .join(', ')}.`
          })(),
        },
        {
          type: 'evidence',
          rows: siteRollups().map((site) => ({
            key: 'site',
            value: `${site.name} · ${site.oee}% · ${site.openAlerts} open`,
          })),
        },
      ],
    },
    {
      id: 'ans_saved',
      mode: 'explore',
      prompt: 'What has this saved us?',
      reasoning: ['queriedAlerts', 'traversedGraph', 'composed'],
      blocks: [
        {
          type: 'text',
          text: (() => {
            const similar = ANALYSIS['ALR-4471']?.similarEvents ?? []
            const avoided = similar.reduce((sum, entry) => sum + (entry.avoidedHours ?? 0), 0)
            const exposure = alerts.reduce(
              (sum, alert) => sum + (alert.downtimeRiskHours ?? 0),
              0,
            )
            return `${Math.round(avoided * 10) / 10} hours of unplanned downtime avoided across ${similar.length} comparable events already closed, and ${Math.round(exposure * 10) / 10} hours currently at risk on the anomalies still open. Those are hours, not money — nothing here prices a stopped line.`
          })(),
        },
      ],
    },
    {
      id: 'ans_draft_wo',
      mode: 'maint',
      prompt: `Draft a work order for ${flagship.assetName}`,
      reasoning: ['resolvedAsset', 'queriedWorkOrders', 'composed'],
      blocks: [
        {
          type: 'text',
          text: `There is already one. ${orders.find((order) => order.alertId === flagship.id)?.id ?? 'A draft'} was raised against ${flagship.id} and is waiting on approval — raising a second would put two jobs on one bearing.`,
        },
        {
          type: 'verdict',
          tone: 'amber',
          text: 'Lockout/tagout is required before this bearing is touched. That sign-off is yours, not mine.',
        },
      ],
    },
  ]
}

/**
 * The ways somebody says hello.
 *
 * Recognising a greeting is NOT the keyword matching this file refuses to do
 * elsewhere. That refusal is about facts: "compressor" appearing in a sentence
 * is not evidence the compressor answer is wanted, and a confident wrong answer
 * about a plant is worse than a miss. A greeting is not a claim about anything,
 * so there is nothing to get wrong — and a bare "hi" answered with "I can't
 * answer that one" is the rudest thing a demo can do.
 */
const GREETINGS = [
  'hi',
  'hey',
  'hello',
  'yo',
  'hiya',
  'howdy',
  'good morning',
  'good afternoon',
  'good evening',
  'morning',
  'evening',
  'hi there',
  'hello there',
  'whats up',
  'sup',
]

/* Not in that list on purpose: "thanks", "ok", "cheers". They are the END of a
   conversation, and answering one with "Hello, I am reading this plant's own
   records" is worse than the honest miss — it reads as an assistant that was
   not listening. */

/**
 * The hello.
 *
 * Says what it is grounded in, with the real counts, then hands over the chips.
 * A greeting that answered with nothing to do next would be a dead end wearing
 * a smile.
 */
function greetingAnswer(mode) {
  const alerts = openAlerts()
  const critical = alerts.filter((alert) => alert.severity === 'critical').length
  const sites = new Set(alerts.map((alert) => alert.siteId)).size

  return {
    id: 'ans_greeting',
    mode: mode ?? 'trouble',
    prompt: 'hello',
    matched: true,
    reasoning: ['queriedAlerts', 'composed'],
    blocks: [
      {
        type: 'text',
        text: `Hello. I am reading this plant's own records — ${alerts.length} anomalies open across ${sites} ${sites === 1 ? 'plant' : 'plants'}, ${critical} of them critical, plus the OEE figures, the work-order board and the operator logbook.`,
      },
      {
        type: 'text',
        text: 'Ask me about a machine, the week\u2019s numbers, or what is waiting on you. I answer a prepared set of questions here rather than running a model, so if I miss one I will say so plainly rather than guess.',
      },
    ],
    suggestions: copilotAnswers()
      .filter((answer) => answer.mode === (mode ?? 'trouble'))
      .map((answer) => answer.prompt)
      .slice(0, 3),
  }
}

/**
 * Answer a question.
 *
 * An exact-ish match against the prepared prompts, and otherwise the fallback —
 * which names what it cannot do instead of improvising. That is the whole
 * behaviour, and it is stated plainly on the screen so nobody in the room is
 * surprised by it.
 *
 * The match is loose about case and punctuation only. It deliberately does NOT
 * keyword-match: "compressor" appearing in a question is not evidence that the
 * asked-for answer is the compressor one, and a wrong confident answer about a
 * plant is worse than an honest miss.
 */
export function answerFor(question, mode) {
  const normalise = (value) =>
    String(value ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9 ]/g, '')
      .replace(/\s+/g, ' ')
      .trim()

  const asked = normalise(question)
  const answers = copilotAnswers()
  const hit = answers.find((answer) => normalise(answer.prompt) === asked)

  if (hit) return { ...hit, matched: true }
  if (GREETINGS.includes(asked)) return greetingAnswer(mode)

  return {
    id: 'ans_fallback',
    mode: mode ?? 'explore',
    prompt: question,
    matched: false,
    reasoning: ['searchedPrepared'],
    /* No blocks. The fallback's wording is FURNITURE — it will say the same
       thing after a real model lands behind this — so it goes through `t()` in
       the page. A fixture cannot translate, and the logbook module already
       leaked an i18n key into a report by trying. */
    blocks: [],
    /* The chips to offer back, in the mode the reader is in — so the fallback
       is a way forward rather than a dead end. */
    suggestions: answers
      .filter((answer) => answer.mode === (mode ?? 'explore'))
      .map((answer) => answer.prompt),
  }
}

/** The prompt chips for a mode. */
export const promptsFor = (mode) =>
  copilotAnswers()
    .filter((answer) => answer.mode === mode)
    .map((answer) => answer.prompt)

/**
 * What the assistant says it is grounded to.
 *
 * The site, not an asset: this screen is opened from the rail with no record in
 * hand. The dock is the one that grounds on a record, and it still does.
 */
export function copilotGrounding() {
  const alerts = openAlerts()
  return {
    scope: 'plant',
    alerts: alerts.length,
    assets: new Set(alerts.map((alert) => alert.assetId)).size,
  }
}
